import API from "../../../Services/BaseUrl";
import { setToken } from "../../../Services/tokenUtils";
import { buildEncryptedLoginPayload, decryptResponseWithAes } from "../../../utils/cryptoUtils";
import { primeKeyCache, clearKeyCache as clearSecureApiKeyCache } from "../../../utils/secureApi";
import { APP_VERSION, APP_CHANNEL } from "../../../version";

// Stable per-browser id used as this device's machine_id for session enforcement.
// Persists across logout (NOT in clearToken's wipe list) so it identifies the browser.
const getDeviceId = () => {
  try {
    let id = localStorage.getItem("deviceId");
    if (!id) {
      id = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem("deviceId", id);
    }
    return id;
  } catch {
    return "web-unknown";
  }
};

// Public IP is captured server-side from HttpServletRequest — no external call needed

// Get browser brand + version from userAgentData.brands
// Filters out generic entries like "Chromium" and "Not_A Brand"
const getBrowserFromBrands = (brands) => {
  if (!brands?.length) return null;
  const real = brands.find(
    (b) => b.brand !== "Chromium" && !b.brand.startsWith("Not")
  );
  if (real) return `${real.brand} ${real.version}`;
  return `${brands[0].brand} ${brands[0].version}`;
};

// Fallback: parse browser from UA string
const getBrowserFromUA = (ua) => {
  if (/Edg\/([\d.]+)/.test(ua)) return "Edge " + RegExp.$1.split(".")[0];
  if (/OPR\/([\d.]+)/.test(ua)) return "Opera " + RegExp.$1.split(".")[0];
  if (/Chrome\/([\d.]+)/.test(ua)) return "Chrome " + RegExp.$1.split(".")[0];
  if (/Firefox\/([\d.]+)/.test(ua)) return "Firefox " + RegExp.$1.split(".")[0];
  if (/Version\/([\d.]+).*Safari/.test(ua)) return "Safari " + RegExp.$1.split(".")[0];
  return "Unknown Browser";
};

// Get system information for login request
const getSystemInfo = async () => {
  const ua = navigator.userAgent;
  const uaData = navigator.userAgentData;

  let systemName = "Unknown";
  let os = "Unknown";
  let systemModel = "Unknown Browser";
  let isMobile = false;

  // Modern approach: navigator.userAgentData (Chromium browsers)
  if (uaData) {
    isMobile = uaData.mobile;
    systemName = uaData.platform || "Unknown";
    systemModel = getBrowserFromBrands(uaData.brands) || getBrowserFromUA(ua);

    try {
      const hints = await uaData.getHighEntropyValues([
        "platformVersion",
        "model",
      ]);

      os = `${uaData.platform} ${hints.platformVersion}`;

      // For mobile devices, use device model as systemName
      if (isMobile && hints.model) {
        systemName = hints.model;
      }
    } catch (e) {
      os = uaData.platform || "Unknown";
    }
  } else {
    // Fallback for Firefox, Safari, etc. (no userAgentData support)
    isMobile =
      /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    systemModel = getBrowserFromUA(ua);

    if (/Android\s([\d.]+)/.test(ua)) {
      os = "Android " + RegExp.$1.split(".")[0];
      const deviceMatch = ua.match(/;\s*([^;)]+)\s*Build\//);
      systemName = deviceMatch ? deviceMatch[1].trim() : "Android Device";
    } else if (/iPhone OS ([\d_]+)/.test(ua)) {
      os = "iOS " + RegExp.$1.replace(/_/g, ".");
      systemName = "iPhone";
      isMobile = true;
    } else if (/iPad/.test(ua)) {
      os = "iPadOS";
      systemName = "iPad";
      isMobile = true;
    } else if (/Windows NT ([\d.]+)/.test(ua)) {
      const ntMap = { "10.0": "10", "6.3": "8.1", "6.2": "8", "6.1": "7" };
      os = "Windows " + (ntMap[RegExp.$1] || RegExp.$1);
      systemName = "Windows";
    } else if (/Mac OS X ([\d_]+)/.test(ua)) {
      os = "MacOS " + RegExp.$1.replace(/_/g, ".");
      systemName = "MacOS";
    } else if (ua.includes("Linux")) {
      os = "Linux";
      systemName = "Linux";
    }
  }

  const systemId = `WEB-${navigator.language}-${window.screen.width}x${window.screen.height}`;

  return {
    systemId,
    systemName,
    os,
    systemModel,
    loginType: isMobile ? "PHONE" : "WEB",
    // Release B session context (spread into the encrypted login payload)
    machineId: getDeviceId(),
    channel: APP_CHANNEL,
    appVersion: APP_VERSION,
  };
};

// Cache the encryption key — prefetched when login page loads
let cachedKeyPromise = null;

const fetchEncryptionKey = async () => {
  const response = await API.post("/users/key");
  return response.data.data; // { id, key }
};

/**
 * Prefetch encryption key when login page loads.
 * Called from Login.jsx useEffect — saves ~400ms on login click.
 */
export const prefetchEncryptionKey = () => {
  cachedKeyPromise = fetchEncryptionKey().catch(() => null);
};

const getCachedOrFetchKey = async () => {
  if (cachedKeyPromise) {
    const cached = await cachedKeyPromise;
    cachedKeyPromise = null; // Use once, then fetch fresh next time
    if (cached) return cached;
  }
  return fetchEncryptionKey();
};

// Server returns this exact message when the cached RSA key was rotated
// while the login page sat idle. We use a substring match because the
// trailing punctuation has changed before.
const isKeyExpiredResponse = (resp) =>
  resp?.errCode === -401
  && /encryption key expired/i.test(resp?.message || resp?.errMsg || "");

/**
 * One round-trip of encrypted login. Returns the decrypted server response
 * (or the raw envelope on a non-encrypted error). Throws only on transport
 * errors (network, timeout, server 5xx).
 */
const performLoginOnce = async (username, password, systemInfo) => {
  const { id, payload, key, aesKey } = await buildEncryptedLoginPayload(
    username,
    password,
    systemInfo,
    getCachedOrFetchKey
  );
  const response = await API.post("/users/login", { id, key, payload });

  if (response.data?.errCode === 0 && response.data?.data?.data) {
    const decrypted = await decryptResponseWithAes(response.data.data.data, aesKey);
    if (decrypted?.data?.token) setToken(decrypted.data.token);
    if (decrypted?.data?.nextKey) primeKeyCache(decrypted.data.nextKey);
    return decrypted;
  }

  // Non-encrypted error envelope (validation errors, key-expired, etc.)
  return response.data;
};

export const loginApi = async (username, password) => {
  try {
    const systemInfo = await getSystemInfo();

    let result = await performLoginOnce(username, password, systemInfo);

    // If the page sat idle long enough for the cached RSA key to be rotated,
    // the server replies with errCode -401 / "Encryption key expired".
    // Silently invalidate every key cache (this module + secureApi) and
    // retry once with a fresh key before bubbling the error to the user.
    if (isKeyExpiredResponse(result)) {
      cachedKeyPromise = null;
      clearSecureApiKeyCache();
      result = await performLoginOnce(username, password, systemInfo);
    }

    return result;
  } catch (error) {
    if (error.code === "ECONNABORTED") {
      throw new Error("Server is not responding (timeout)");
    }
    if (error.message?.includes("Network Error")) {
      throw new Error("Cannot reach server");
    }
    throw new Error(error.response?.data?.message || error.response?.data?.errMsg || "Login failed");
  }
};
